import { useQuery } from "@tanstack/react-query"
import { api } from "@/app/api/client"

export type Role = "owner" | "editor" | "viewer"

export interface CurrentUserClient {
  name: string
  slug: string
  role: Role
}

export interface CurrentUser {
  id: string
  name: string
  role: Role
  email_address: string
  superadmin: boolean
  avatar_url: string | null
  /** The tenant the user belongs to — `name` is the admin-set Client.name. */
  client: { id: string; name: string }
  /** Every client this identity can access (all of them, for superadmins) — powers the dropdown's client switcher. */
  clients: CurrentUserClient[]
}

/**
 * Loads the authenticated user (GET /me → { id, name, role, email_address,
 * superadmin, avatar_url, client, clients }) and derives authorization tiers:
 * - `canWrite` — owner/editor/superadmin may mutate; viewers are read-only.
 * - `isStaff` — superadmin-only gate for the MANAGE section (import,
 *   county/OCC reference data), i.e. LandTrack staff.
 * - `canManageUsers` — Company Settings; owners and superadmins.
 */
export function useCurrentUser() {
  const {
    data: user,
    isLoading,
    error,
  } = useQuery<CurrentUser>({
    queryKey: ["me"],
    queryFn: () => api.get<CurrentUser>("/me"),
    retry: false,
  })

  const role = user?.role
  const canWrite = !!user && (user.superadmin || role === "owner" || role === "editor")
  // MANAGE section (import, county/OCC reference data) — LandTrack staff only.
  const isStaff = !!user?.superadmin
  // Company Settings — owners and staff.
  const canManageUsers = !!user && (user.superadmin || role === "owner")

  return { user, role, canWrite, isStaff, canManageUsers, isLoading, error }
}
